"use client"

import { useState, useEffect } from "react"
import { X, AlertTriangle } from "lucide-react"
import {
  dismissReminderForToday,
} from "@/lib/reminder"
import { getTodayDateString } from "@/lib/date-utils"
import { getCzechDaysText, cn } from "@/lib/utils"
import { motion, AnimatePresence } from "framer-motion"
import type { DailyEntry } from "@/lib/db/schema"

interface ReminderBannerProps {
  todayEntry?: DailyEntry
  currentStreak?: number
}

export function ReminderBanner({ todayEntry, currentStreak = 0 }: ReminderBannerProps) {
  const [show, setShow] = useState(false)

  // Determine if streak is in threat (has streak but haven't logged today)
  const inThreat = currentStreak > 0 && !todayEntry

  useEffect(() => {
    // Check if reminder should be shown
    // Show if: no entry today AND not dismissed today
    if (typeof window === "undefined") return

    const today = getTodayDateString()
    const dismissedToday = localStorage.getItem(`reminder-dismissed-${today}`)

    setShow(!todayEntry && !dismissedToday)
  }, [todayEntry])

  const handleDismiss = () => {
    dismissReminderForToday()
    setShow(false)
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={cn(
            "rounded-lg p-4 mb-4 border",
            inThreat
              ? "bg-gradient-to-r from-yellow-400/20 to-amber-400/15 border-yellow-500/40"
              : "bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20"
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              {/* Warning icon for threat state */}
              {inThreat && (
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="flex-shrink-0 mt-0.5"
                >
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                </motion.div>
              )}

              <div className="flex-1">
                <h3 className="font-medium mb-1">
                  {inThreat
                    ? `Pozor! Tvoje série ${currentStreak} ${getCzechDaysText(currentStreak)} je v ohrožení!`
                    : "Ještě jsi dnes nezaznamenal/a svou energii"
                  }
                </h3>
                <p className="text-sm text-muted-foreground">
                  {inThreat
                    ? "Zaznamenej svou energii ještě dnes, aby ti série nepřerušila."
                    : "Jak se dnes cítíš?"
                  }
                </p>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label="Zavřít připomínku"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
